import { msg } from "@omegagrid/localize";
import { ColumnSource, RowSource } from "@omegagrid/grid";
import rp, { GridEditorAdapter, GridEditorOptions } from "@reporting/rp-app-host";
import { UsageRow } from "@reporting/rp-types/ai";
import { UsageRange } from "../types";

const PAGE_SIZE = 500;

export class UsageGridAdapter extends GridEditorAdapter {

	private _columns: ColumnSource[];
	private _rows: RowSource[];

	constructor(
		private range: UsageRange = '7d'
	) { super() }

	async getColumns(): Promise<ColumnSource[]> {
		if (!this._columns) await this._load();
		return this._columns;
	}

	async getRows(): Promise<RowSource[]> {
		await this._load();
		return this._rows;
	}

	/** Fetches the time series once and builds both the pivoted columns and the tree rows. */
	private async _load(): Promise<void> {
		
		const data = await rp.ms('ai').get<UsageRow[]>(`usage/${this.range}`);
		// distinct buckets, chronologically — both 'YYYY-MM-DD' and 'YYYY-MM' sort lexicographically
		const buckets = [...new Set(data.map(r => r.period).filter((p): p is string => !!p))].sort();

		this._columns = [{
			data: 'company',
			header: msg('Company'),
			t: 't',
			levelIndent: true,
			levelButtons: true,
			w: 220,
		}, ...buckets.map((bucket): ColumnSource => ({
			data: bucket,
			header: this._formatBucket(bucket),
			t: 'n',
			w: 90,
		}))];

		// nest source rows: company -> user -> bucket
		type UserEntry = { userName?: string; loginName?: string; buckets: Map<string, number> };
		type CompanyEntry = { accounting: number; company?: string; users: Map<string, UserEntry> };
		const byCompany = new Map<number, CompanyEntry>();
		for (const r of data) {
			if (r.accounting == null) continue;
			let company = byCompany.get(r.accounting);
			if (!company) {
				company = { accounting: r.accounting, company: r.company, users: new Map() };
				byCompany.set(r.accounting, company);
			}
			const userKey = r.user_name ?? '';
			let user = company.users.get(userKey);
			if (!user) {
				user = { userName: r.user_name, loginName: r.login_name, buckets: new Map() };
				company.users.set(userKey, user);
			}
			if (r.period) user.buckets.set(r.period, (user.buckets.get(r.period) ?? 0) + (r.total_tokens ?? 0));
		}

		// company (l:0, combined total) -> user rows (l:1)
		const rows: RowSource[] = [];
		for (const company of byCompany.values()) {
			const companyData: Record<string, unknown> = {
				company: `${company.accounting} ${company.company ?? ''}`.trim(),
			};
			for (const user of company.users.values()) {
				for (const [bucket, tokens] of user.buckets) {
					companyData[bucket] = ((companyData[bucket] as number) ?? 0) + tokens;
				}
			}
			rows.push({ data: companyData, l: 0 });

			for (const user of company.users.values()) {
				const child: Record<string, unknown> = {
					company: user.loginName || user.userName || '',
				};
				for (const [bucket, tokens] of user.buckets) {
					child[bucket] = tokens;
				}
				rows.push({ data: child, l: 1 });
			}
		}

		this._rows = rows;
	}

	/** 'YYYY-MM-DD' => 'DD.MM.', 'YYYY-MM' => 'MM/YYYY' */
	private _formatBucket(bucket: string): string {
		const parts = bucket.split('-');
		return parts.length === 3
			? `${parts[2]}.${parts[1]}.`
			: `${parts[1]}/${parts[0]}`;
	}

	getOptions(): Partial<GridEditorOptions> {
		return this.createOptions({
			colLetters: false,
			columnSorting: true,
			filters: true,
			pageSize: PAGE_SIZE,
			editable: false,
			lightTheme: null,
			darkTheme: null,
		});
	}

}
